import React, { useMemo } from 'react';

import Icon, { ICONS, COLORS } from '../general-components/icon';
import Avatar from '../general-components/avatar';
import { Profile } from '../../stores/reducers/invitations';
import { FEED_TYPES } from '../../stores/reducers/shots';
import { valueof } from '../../stores/types/types';

interface FeedIndicatorIconProps {
	name: valueof<typeof ICONS>;
}

function FeedIndicatorIcon({ name }: FeedIndicatorIconProps) {
	return <Icon name={name} color={COLORS.GRAY_PRESENTER} size={16} />;
}

interface HiddenFeedIndicatorProps {
	feedType: valueof<typeof FEED_TYPES>;
	imageUrl?: string;
	title?: string;
	userProfile?: Profile | null;
}

export default function HiddenFeedIndicator({
	feedType,
	imageUrl,
	title,
	userProfile,
}: HiddenFeedIndicatorProps): JSX.Element {
	const isTitleFeedType = useMemo((): boolean => {
		const titleFeedTypes: (typeof feedType)[] = [
			FEED_TYPES.PRESENTER,
			FEED_TYPES.EMAIL,
			FEED_TYPES.MEDIA,
		];

		return titleFeedTypes.includes(feedType);
	}, [feedType]);

	const feedImage = useMemo((): JSX.Element => {
		if (userProfile && isTitleFeedType) {
			return <Avatar user={userProfile} noBorder={true} size={'twentyfour'} />;
		}

		if (feedType === FEED_TYPES.MEDIA) {
			return imageUrl ? (
				<img src={imageUrl} alt={title} />
			) : (
				<FeedIndicatorIcon name={ICONS.TAB_MEDIA} />
			);
		}

		// Just feedType === FEED_TYPES.EMPTY needed, but covers all
		return <FeedIndicatorIcon name={ICONS.ADD_INVITE} />;
	}, [feedType, imageUrl, isTitleFeedType, title, userProfile]);

	const feedTitle = useMemo((): string => {
		if (title && isTitleFeedType) {
			return title;
		}

		return 'Audio Feed';
	}, [feedType, isTitleFeedType, title]);

	return (
		<div className="hidden-feed-indicator">
			<div className="hidden-feed-indicator__image">
				<div
					className="round-green-outline"
					data-testid="hidden-feed-indicator__image"
				>
					{feedImage}
				</div>
			</div>
			<div
				className="hidden-feed-indicator__title"
				title={feedTitle}
				data-testid="hidden-feed-indicator__title"
			>
				{feedTitle}
			</div>
		</div>
	);
}
